// Top-level + route-level error boundary for the app.
//
// Deliberately hand-rolled (not @sentry/react's ErrorBoundary) for two reasons:
//   1. The boundary's own code must be EAGER (never a lazy chunk) or it can't
//      reliably catch a chunk-load failure. Hand-rolling keeps it dependency-free
//      in the entry bundle.
//   2. Sentry stays LAZY: we import("@sentry/react") only inside componentDidCatch
//      (mirrors setSentryUser in lib/sentry-init.ts), so the SDK never enters the
//      eager bundle, and we choose info-level for a handled chunk-reload vs
//      captureException for a real crash — instead of Sentry's boundary
//      auto-capturing every error as an exception.
//
// Two mount points, one class, selected via `variant`:
//   - "inline": outermost in main.tsx, OUTSIDE MantineProvider — inline-styled
//     fallback so it renders even if a provider itself threw.
//   - "route": around the shell <Outlet/>, INSIDE the providers — Mantine Alert
//     fallback matching DocsArticle's ErrorFallback; the header/sidebar stay.
//
// See docs/adr/0081-frontend-error-boundary-and-chunk-recovery.md.
import { Component, type ReactNode } from "react";
import { Alert, Button, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";

import { isChunkLoadError } from "@/lib/isChunkLoadError";
import { triggerChunkReload } from "@/lib/chunkReload";

type Variant = "inline" | "route";

interface Props {
  children: ReactNode;
  variant: Variant;
  /**
   * When any entry changes while the boundary is showing an error, the error is
   * cleared (e.g. `resetKeys={[pathname]}` so navigating away un-sticks a
   * crashed route). No effect while there is no error.
   */
  resetKeys?: unknown[];
}

interface State {
  hasError: boolean;
  isChunk: boolean;
  willReload: boolean;
}

const INITIAL: State = { hasError: false, isChunk: false, willReload: false };

function resetKeysChanged(a?: unknown[], b?: unknown[]): boolean {
  if (a === b) return false;
  if (!a || !b || a.length !== b.length) return true;
  for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return true;
  return false;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = INITIAL;

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const isChunk = isChunkLoadError(error);
    // Optimistically assume a chunk error will reload; componentDidCatch
    // downgrades `willReload` to false if the one-reload guard already fired.
    return { hasError: true, isChunk, willReload: isChunk };
  }

  componentDidCatch(error: unknown): void {
    if (isChunkLoadError(error)) {
      const willReload = triggerChunkReload();
      if (!willReload) this.setState({ willReload: false });
      return;
    }
    // Real render crash — report to Sentry (lazy import; never throw from here).
    import("@sentry/react")
      .then((Sentry) => Sentry.captureException(error))
      .catch(() => {
        /* observability mishap must not break the fallback */
      });
  }

  componentDidUpdate(prev: Props): void {
    if (
      this.state.hasError &&
      resetKeysChanged(prev.resetKeys, this.props.resetKeys)
    ) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState(INITIAL);
  };

  render(): ReactNode {
    const { hasError, isChunk, willReload } = this.state;
    if (!hasError) return this.props.children;

    // Chunk error we're about to auto-reload from: a brief neutral "updating"
    // state, never an alarming error card.
    if (isChunk && willReload) return <UpdatingScreen />;

    return this.props.variant === "inline" ? (
      <InlineErrorScreen />
    ) : (
      <RouteErrorCard onRetry={this.reset} />
    );
  }
}

function prefersDark(): boolean {
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  } catch {
    return false;
  }
}

/** Neutral, provider-free "we're reloading" flash. Used for both variants. */
function UpdatingScreen() {
  const dark = prefersDark();
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: dark ? "#101010" : "#FAFAFA",
        color: dark ? "#bbb" : "#555",
        fontFamily: "Google Sans Flex, Google Sans, sans-serif",
        fontSize: 16,
        zIndex: 9999,
      }}
    >
      Updating to the latest version…
    </div>
  );
}

/**
 * Outermost fallback — MUST NOT depend on Mantine/theme providers (they may be
 * the thing that threw). Inline styles only; legible in light and dark.
 */
function InlineErrorScreen() {
  const dark = prefersDark();
  const bg = dark ? "#101010" : "#FAFAFA";
  const fg = dark ? "#e6e6e6" : "#1a1a1a";
  const sub = dark ? "#9a9a9a" : "#666";
  const btnBg = "#3455D3";
  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        padding: 24,
        textAlign: "center",
        background: bg,
        color: fg,
        fontFamily: "Google Sans Flex, Google Sans, sans-serif",
        zIndex: 9999,
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 500 }}>Something went wrong</div>
      <div style={{ fontSize: 15, color: sub, maxWidth: 420, lineHeight: 1.5 }}>
        Reload the page to continue. If it keeps happening, please contact
        support.
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          marginTop: 4,
          padding: "10px 20px",
          fontSize: 15,
          fontWeight: 500,
          color: "#fff",
          background: btnBg,
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}

/** Route-level fallback — inside providers, so it uses Mantine (matches
 *  DocsArticle's ErrorFallback). Header/sidebar remain around it. */
function RouteErrorCard({ onRetry }: { onRetry: () => void }) {
  return (
    <Stack gap="sm" maw={560} mx="auto" my="xl" px="md" role="alert">
      <Alert
        icon={<IconAlertTriangle />}
        title="Something went wrong"
        color="red"
      >
        <Text size="sm">
          Something went wrong while loading this page. Try again — if it keeps
          happening, please contact support.
        </Text>
      </Alert>
      <Button variant="light" onClick={onRetry} style={{ alignSelf: "flex-start" }}>
        Try again
      </Button>
    </Stack>
  );
}
